import { CustomWidgetCollection, JsonObject, JsonObjectProperty, Question } from 'survey-core';
import CustomWidget from './CustomWidget';
import { Class2MedDevModel } from './Class2MedDevModel';
import Modal from '../../components/Modal';

interface HBSSurvey extends Class2MedDevModel {
  customHBSOnComplete?: () => void;
}

export interface HBSQuestion extends Question {
  survey: HBSSurvey;
  actionType: ActionType;
}

const ACTION_TYPE = 'actionType';

const ACTION_TYPE_CHOICES = ['Create task'] as const;

type ActionType = (typeof ACTION_TYPE_CHOICES)[number];

export const surveyActionProperties = [
  {
    name: ACTION_TYPE,
    choices: ACTION_TYPE_CHOICES.map((choice) => ({ value: choice, text: choice })),
    category: 'general',
    visibleIndex: 2
  },
  {
    name: 'taskQueue',
    choices: ['HBS queue', 'Client queue'],
    default: 'HBS queue',
    dependsOn: ACTION_TYPE,
    visibleIf: (obj) => obj.actionType === 'Create task',
    category: 'general',
    visibleIndex: 6
  },
  {
    name: 'taskType',
    choices: ['Red flag'],
    default: 'Book Appointment',
    dependsOn: ACTION_TYPE,
    visibleIf: (obj) => obj.actionType === 'Create task',
    category: 'general',
    visibleIndex: 3
  },
  {
    name: 'taskDeadlineInHours',
    type: 'number',
    default: 0,
    dependsOn: ACTION_TYPE,
    visibleIf: (obj) => obj.actionType === 'Create task',
    category: 'general',
    visibleIndex: 4
  },
  {
    name: 'taskNotes',
    type: 'text',
    default: '',
    dependsOn: ACTION_TYPE,
    visibleIf: (obj) => obj.actionType === 'Create task',
    category: 'general',
    visibleIndex: 5
  }
] satisfies CustomizeSurveyActionJsonObjectProperty[];

/** Renders a custom action button which is used to interpret what outcome is necessary from the survey inputs and leads to the appropriate action on button press. */
const CustomizeClass2MedDevSurveyAction = () => {
  const name = 'action';

  if (CustomWidgetCollection.Instance.getCustomWidgetByName(name)) {
    return;
  }

  const afterRenderHandler = (question: HBSQuestion, el: HTMLElement) => {
    const message = el.getElementsByTagName('div')[0];
    message.innerHTML = question.html;
    message.style.whiteSpace = 'normal';

    const actionButton = el.getElementsByTagName('button')[0] || el.appendChild(document.createElement('button'));
    actionButton.classList.add('sd-btn');
    actionButton.classList.add('sd-btn--action');

    if (question.value) {
      disableActionButton(actionButton);
    } else {
      actionButton.innerText = question.buttonText;
    }

    actionButton.onclick = async () => {
      const { actionType, taskType, survey } = question;

      if (survey.currentPage.hasErrors(true, true)) return;

      if (actionType === 'Create task' && taskType === 'Red flag') {
        console.log('Special case: Creating red-flagged task!');

        // Check that you are running with the model callbacks in place
        if (!survey.handleRedFlagAction) {
          Modal.error({
            title: 'Missing Handler',
            message:
              'An unexpected technical error has occurred and we have been unable to fulfill your request. Please contact HBS support in order to take appropriate steps for your care.'
          });
          return;
        }

        return survey.handleRedFlagAction(question, actionButton);
      }

      // Check that you are running with the model callbacks in place
      if (!survey.handleAction) {
        Modal.error({
          title: 'Missing Handler',
          message:
            'An unexpected technical error has occurred and we have been unable to fulfill your request. Please contact HBS support in order to take appropriate steps for your care.'
        });
        return;
      }

      return survey.handleAction(question, actionButton);
    };

    const onValueChangedCallback = () => {
      message.innerHTML = question.html ? question.html : '';
      if (question.value) {
        disableActionButton(actionButton);
      } else {
        actionButton.innerText = question.buttonText;
      }
    };

    const onReadOnlyChangedCallback = () => {
      if (question.isReadOnly || question.value) {
        message.setAttribute('disabled', 'disabled');
        actionButton.setAttribute('disabled', 'disabled');
      } else {
        message.removeAttribute('disabled');
        actionButton.removeAttribute('disabled');
      }
    };
    question.readOnlyChangedCallback = onReadOnlyChangedCallback;

    question.onPropertyChanged.add(onValueChangedCallback);
    onValueChangedCallback();
    onReadOnlyChangedCallback();
  };

  JsonObject.metaData.addClass(name, [], undefined, 'html');
  JsonObject.metaData.addProperties(name, surveyActionProperties);

  const taskTypeProperty = JsonObject.metaData.findProperty(name, 'taskType');
  const baseSetChoices = taskTypeProperty.setChoices.bind(taskTypeProperty);

  taskTypeProperty.setChoices = (value: Array<any>, ...rest) => {
    if (!value.includes('Red flag')) {
      value.push('Red flag');
    }
    return baseSetChoices(value, ...rest);
  };

  CustomWidget({
    name,
    title: 'Action',
    iconName: `hbs-${name}-class`,
    activatedByChangeHandler: () => {},
    htmlTemplate: `<div data-testid="customized-${name}-component"><div></div><button></button></div>`,
    afterRenderHandler
  });
};

export default CustomizeClass2MedDevSurveyAction;

type CustomizeSurveyActionJsonObjectProperty = Omit<Partial<JsonObjectProperty>, 'visibleIf'> & {
  choices?: string[] | { value: string; text: string }[];
  visibleIf?: (obj: { actionType: ActionType; appointmentType: string }) => boolean;
};

export function disableActionButton(actionButton: HTMLButtonElement) {
  actionButton.innerText = 'Submitted';
  actionButton.style.backgroundColor = '#aaa';
  actionButton.style.cursor = 'default';
  actionButton.disabled = true;
}
